import {
  createFileRoute,
  Navigate,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import React from "react";
import { Box } from "@salestrak/ui";

import { useWorkspace, type Workspace } from "#app/lib/data/workspace-context";
import { SalesTrakLoading } from "#app/components/brand/salestrak-logo";
import { NetworkErrorScreen } from "#app/components/app-error-screens";
import { WorkspaceInviteScreen } from "#app/components/workspace-invite/workspace-invite-screen";
import { pendingInviteIds } from "#app/lib/workspace-invites";
import { MobileAppNotice } from "#app/components/mobile-app-notice";

/**
 * The invite gate: which pending invite, if any, stands in front of the app
 * right now. The set is latched when the workspace list finishes loading and
 * re-armed each time it loads again (first paint, sign-in, a workspace
 * switch) — an invite that arrives mid-session must not blank the workspace
 * someone is working in; it waits for the next load and lists in the picker
 * meanwhile. Deciding an invite drops it from the queue; the next one, if
 * any, follows (the refetch after a decision does not pass through loading,
 * so the queue is what advances it).
 */
function useInviteGate(workspaces: Workspace[], ready: boolean) {
  const latched = React.useRef<string[] | null>(null);
  if (!ready) {
    // Signed out, or a load in flight: whatever was latched belongs to the
    // previous list. Re-arm so the next loaded list is read fresh.
    latched.current = null;
  } else if (latched.current === null) {
    latched.current = pendingInviteIds(workspaces);
  }
  const [decided, setDecided] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const nextId = latched.current?.find((id) => !decided.has(id));
  // Re-read from the live list: a row accepted elsewhere (another tab) is no
  // longer `invited`, and the gate lets it through.
  const invite = nextId
    ? workspaces.find(
        (w) => w.workspaceId === nextId && w.memberStatus === "invited",
      )
    : undefined;
  const markDecided = React.useCallback((id: string) => {
    setDecided((prev) => new Set(prev).add(id));
  }, []);
  return { invite, markDecided };
}

function AppLayout() {
  const {
    activeWorkspace,
    isLoading,
    error,
    workspaces,
    acceptInvitation,
    declineInvitation,
    switchWorkspace,
  } = useWorkspace();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { invite, markDecided } = useInviteGate(
    workspaces,
    !isLoading && !error,
  );

  // A link or bookmark names its workspace in the path (`/<slug>/...`). With
  // no workspace chosen yet on this device (a new browser, a fresh log in)
  // that workspace opens, instead of the picker.
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  const addressed =
    !isLoading && !activeWorkspace
      ? workspaces.find(
          (w) => w.urlPath === firstSegment && w.memberStatus === "active",
        )
      : undefined;
  React.useEffect(() => {
    if (addressed) void switchWorkspace(addressed);
  }, [addressed, switchWorkspace]);

  if (isLoading || addressed) {
    return (
      <Box fill="flex" align="center" justify="center">
        <SalesTrakLoading />
      </Box>
    );
  }

  // `error` is the init failure: the account/workspace fetch itself died, so
  // every screen below would render against nothing. One gate here covers the
  // whole (app) group.
  if (error) {
    return <NetworkErrorScreen />;
  }

  // A pending invite is decided before anything else renders — ahead of the
  // picker redirect too, so a user whose only workspace is the invited one
  // lands here rather than on an empty picker.
  if (invite) {
    const id = invite.workspaceId;
    return (
      <WorkspaceInviteScreen
        workspace={invite}
        via="gate"
        onJoin={async () => {
          // acceptInvitation refetches and switches the active workspace;
          // the index route then lands on the user's default page there.
          await acceptInvitation(id, "gate");
          markDecided(id);
          navigate({ to: "/", replace: true });
        }}
        onDecline={async () => {
          await declineInvitation(id, "gate");
          markDecided(id);
        }}
      />
    );
  }

  if (!activeWorkspace) {
    const lastSegment = pathname.split("/").filter(Boolean).pop();
    if (
      lastSegment !== "workspace-select" &&
      lastSegment !== "workspace-create" &&
      lastSegment !== "admin"
    ) {
      return <Navigate to="/workspace-select" />;
    }
  }

  return (
    <>
      <Outlet />
      <MobileAppNotice />
    </>
  );
}

export const Route = createFileRoute("/(app)")({ component: AppLayout });
